// Central TanStack Query client factory.
//
// This only wires up the cache/defaults - it deliberately does NOT touch any
// existing `fetch`/`useEffect` data flow. Existing code keeps working untouched;
// new features can opt into `useQuery`/`useMutation` when ready.
//
// Uses the Next.js App Router pattern: one client per request on the server,
// and a lazily-created singleton in the browser so navigations reuse the cache.

import {
  QueryClient,
  defaultShouldDehydrateQuery,
  isServer,
} from "@tanstack/react-query";

function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Data is considered fresh for 60s before a background refetch.
        staleTime: 60 * 1000,
        // Keep unused data around for 5 minutes before garbage collection.
        gcTime: 5 * 60 * 1000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
      dehydrate: {
        // Allow pending queries to be dehydrated for streaming SSR later on.
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) ||
          query.state.status === "pending",
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined;

// Returns a QueryClient: a fresh one per request on the server, a cached
// singleton in the browser (guards against React re-render churn).
export function getQueryClient(): QueryClient {
  if (isServer) return makeQueryClient();
  if (!browserQueryClient) browserQueryClient = makeQueryClient();
  return browserQueryClient;
}
